import React from "react";
import {
  Bus,
  CircleDollarSign,
  CircleHelp,
  Hourglass,
  Landmark,
  MapPinOff,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SettlementSummaryBar } from "../../settlement/components/SettlementSummaryBar";
import { StatCard } from "../../settlement/components/StatCard";
import {
  PartnerHotelRevenueRank,
  PartnerTotalRevenueSummary,
} from "@/types/partner-dashboard";
import type { TenantPartnerOrdersSummary } from "@/types/partner-order";
import { mapPartnerSummaryMoney } from "../utils/partnerSummaryMoney";

const statBadgeClassName =
  "inline-flex max-w-full w-fit items-center justify-center rounded-md bg-royal-glow-5 px-2 py-0.5 text-2xs font-semibold leading-none text-royal-glow-2";

function getCurrencySymbol(currency: string): string {
  if (currency === "EUR") return "€";
  if (currency === "USD") return "$";
  if (currency === "GBP") return "£";
  return `${currency} `;
}

function formatRevenueValue(value: number, currency: string): string {
  return `${getCurrencySymbol(currency)}${value.toLocaleString("en-GB")}`;
}

type GuesteriaPartnersRevenueStatsProps = {
  topRevenueHotel: PartnerHotelRevenueRank | null;
  lowestRevenueHotel: PartnerHotelRevenueRank | null;
  totalRevenueSummary: PartnerTotalRevenueSummary | null;
  /** Carries cancelledOrders/refundedAmount — absent from PartnerTotalRevenueSummary. */
  ordersSummary?: TenantPartnerOrdersSummary | null;
};

export const GuesteriaPartnersRevenueStats = ({
  topRevenueHotel,
  lowestRevenueHotel,
  totalRevenueSummary,
  ordersSummary,
}: GuesteriaPartnersRevenueStatsProps): React.JSX.Element => {
  const { t } = useTranslation();
  // Only the hotel's share is shown (R-098): Total = Earned + Expected. The
  // orders summary comes first; the total-revenue summary is the fallback.
  const moneySource = ordersSummary ?? totalRevenueSummary;
  const money = moneySource ? mapPartnerSummaryMoney(moneySource) : null;
  // Refunded is a count of orders: the refunded order sum is not a share.
  const refundedValue =
    ordersSummary?.refundedOrders === undefined
      ? "-"
      : ordersSummary.refundedOrders;

  return (
    <SettlementSummaryBar contentClassName="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
      <StatCard
        icon={<Landmark className="text-light-stone size-6" />}
        label={topRevenueHotel?.hotelName ?? "-"}
        value={
          topRevenueHotel
            ? formatRevenueValue(
                topRevenueHotel.totalRevenue,
                totalRevenueSummary?.currency ?? "EUR",
              )
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.highestRevenueMonth")}
          </span>
        }
      />
      <StatCard
        icon={<Landmark className="text-light-stone size-6" />}
        label={t("billing.guesteriaPartnersRevenue.total")}
        value={
          money ? formatRevenueValue(money.total, money.currency ?? "EUR") : "-"
        }
      />
      <StatCard
        icon={<CircleDollarSign className="text-light-stone size-6" />}
        label={t("partners.detail.earned")}
        value={
          money
            ? formatRevenueValue(money.earned, money.currency ?? "EUR")
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.forMonth")}
          </span>
        }
      />
      <StatCard
        icon={<Hourglass className="text-light-stone size-6" />}
        label={t("partners.detail.expected")}
        value={
          totalRevenueSummary
            ? formatRevenueValue(
                totalRevenueSummary.pendingEarnings,
                totalRevenueSummary.currency ?? "EUR",
              )
            : "-"
        }
      />
      <StatCard
        icon={<Bus className="text-light-stone size-6" />}
        label={lowestRevenueHotel?.hotelName ?? "-"}
        value={
          lowestRevenueHotel
            ? formatRevenueValue(
                lowestRevenueHotel.totalRevenue,
                totalRevenueSummary?.currency ?? "EUR",
              )
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.lowestRevenue")}
          </span>
        }
      />
      <StatCard
        icon={<MapPinOff className="text-light-stone size-6" />}
        label={t("billing.guesteriaPartnersRevenue.outOfCatalog.label")}
        value={
          money
            ? t("billing.guesteriaPartnersRevenue.outOfCatalog.value", {
                count: money.outOfCatalog.count,
                earned: formatRevenueValue(
                  money.outOfCatalog.earned,
                  money.currency ?? "EUR",
                ),
                expected: formatRevenueValue(
                  money.outOfCatalog.expected,
                  money.currency ?? "EUR",
                ),
              })
            : "-"
        }
      />
      <StatCard
        icon={<XCircle className="text-light-stone size-6" />}
        label="Cancelled orders"
        value={ordersSummary ? ordersSummary.cancelledOrders : "-"}
      />
      <StatCard
        icon={<RotateCcw className="text-light-stone size-6" />}
        label="Refunded"
        value={refundedValue}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            <StatCard
              icon={<CircleHelp className="text-light-stone size-6" />}
              label="Estimated"
              value={ordersSummary ? ordersSummary.estimatedOrders : "-"}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {t("billing.partnerOrders.estimatedTooltip")}
        </TooltipContent>
      </Tooltip>
    </SettlementSummaryBar>
  );
};
